import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from '@angular/router';
import { CoreService, DialogResult, DialogService, DspxCardSetBaseFormComponent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { FILTER_QUESTION_CODES, NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, SECTION_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES } from '../../constants/templates.constants';
import { Mapper, MapperBuilder, TRANSFORM } from '../../helpers/mapper';
import { VptasInvoiceGridFilterRequest } from '../../interfaces/vptas-invoice-grid-filter-request.interface';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { ResponseParserService } from '../../services/response-parser.service';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-vptas-invoice-list',
  templateUrl: './vptas-invoice-list.component.html',
  styleUrl: './vptas-invoice-list.component.scss'
})
export class VptasInvoiceListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {
  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  filterQuestionToFilterModelMapper: Mapper;
  activeDialog: string = '';
  private routePrefix = '';
  private applicationId = '';
  private contactId = '';
  private isApplicationGrid = !!this.applicationId || !!this.contactId;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private coreService: CoreService,
    private errorService: ErrorService,
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.vptasInvoiceList,
      () =>{
        const paramMap = this.route.snapshot.paramMap;
        const applicationId = paramMap.get('id');
        const contactId = paramMap.get('contactId');
        if (applicationId) {
          return API_ENDPOINTS.dashboard.getVptasInvoicesByApplication;
        }
        if (contactId) {
          return API_ENDPOINTS.dashboard.getVptasInvoicesByContact;
        }
        return API_ENDPOINTS.dashboard.getVptasInvoices;
      },
      false);

    this.filterQuestionToFilterModelMapper =
      new MapperBuilder()
        .WithDefaultTargetFieldMapping(/Filter$/i, "") // When target field name is not specified, just use the source field's name, but drop "Filter" from the end
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.oracleVendorNumberFilter)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.patientContactIdFilter)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.patientFirstNameFilter)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.patientSurnameFilter)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.patientDobFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.invoiceNumberFilter)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.invoiceDateFromFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.invoiceDateToFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.paymentNumberFilter)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.invoiceDescriptionFilter)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.paymentDateFromFilter, TRANSFORM.STRING_TO_DATE)
        .MapFrom(FILTER_QUESTION_CODES.vptasInvoiceListForm.paymentDateToFilter, TRANSFORM.STRING_TO_DATE)
        .BuildMapper();
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const applicationId = paramMap.get('id');
        const contactId = paramMap.get('contactId');
        this.applicationId = applicationId ?? '';
        this.contactId = contactId ?? '';
        this.isApplicationGrid =  !!this.applicationId || !!this.contactId;
      })
    );

    this.coreService.conditionFunctions = 
    {
      showInvoiceSearchFilter: () => !this.isApplicationGrid,
      showInvoiceByApplicationFilter: () => this.isApplicationGrid,
      showApplicationDetail: () => this.isApplicationGrid,
      showPlacementDetail: () => this.isApplicationGrid,
      showBackButton: () => this.isApplicationGrid,
      showInvoiceGrid: () => !this.isApplicationGrid,
      showInvoiceGridForPlacement: () =>  this.isApplicationGrid,
      showApplicationID: () => this.showApplicationID(),
    };

    this.coreService.applicationFunctions = {
      exit: () => this.navigateBack(),
      showBackButton: () => this.isApplicationGrid,
    };
  }
  showApplicationID() {
    return  this.applicationId !== '' ;
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoad(event: any): void {
     this.loadGridDataOnFormLoad(this.getInvoiceFilterRequest());
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.vptasInvoiceListForm.invoiceSearchGridFilter:
      case QUESTION_CODES.vptasInvoiceListForm.invoiceGridFilter:
        {
          const filterRequest = this.getInvoiceFilterRequest();
          if (filterRequest.invoiceDateFrom 
            && filterRequest.invoiceDateTo 
            && filterRequest.invoiceDateFrom > filterRequest.invoiceDateTo) 
            {
              this.dialogService.showDialog(
                POP_UP_CODES.vptasInvoiceList.invalidDateRange)  
              return;
            }
          this.loadGridData(filterRequest);
          break;
        }
    }
  }

  actionButton_invoiceGrid_datagrid_cancelInvoice_Clicked(event: any) {
    this.activeDialog = POP_UP_CODES.vptasInvoiceList.cancelInvoice;
    this.dialogService.showDialog(this.activeDialog, event.event.data);
  }

  actionButton_invoiceSearchGrid_datagrid_colLink_eppApplicationId_Clicked(event: any): void {
    const navExtras = this.getNavigationExtras();
    this.router.navigate(['/', ROUTE_CONSTANTS.vptasApplication.prefix, event.event.data.applicationId], navExtras);
  }

  dialogButton_cancelInvoice_Clicked(event: any) {
    this.activeDialog = POP_UP_CODES.vptasInvoiceList.confirmCancelInvoice;
    this.registerSubscription(
      this.dialogService
        .showConfirm(this.activeDialog)
        .subscribe((response) => {
          if (response === DialogResult.ACCEPTED) {
            this.spinnerService.showSpinner();
            this.appDataService
              .post(API_ENDPOINTS.vptasApplication.cancelInvoice, { invoiceId: event.id, cancellationReason: event.cancellationReason })
              .pipe(
                take(1),
                this.errorService.errorHandler(),
                finalize(() => this.spinnerService.hideSpinner())
              )
              .subscribe((data) => {
                this.loadGridDataOnFormLoad(this.getInvoiceFilterRequest());
              });
          }
        })
    );
  }

  private getInvoiceFilterRequest(): VptasInvoiceGridFilterRequest {

    let filter =  this.isApplicationGrid
      ? QUESTION_CODES.vptasInvoiceListForm.invoiceGridFilter
      : QUESTION_CODES.vptasInvoiceListForm.invoiceSearchGridFilter;
    const filterModel = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.vptasInvoiceListForm.invoices,
      PAGE_NUMBERS.vptasInvoiceListForm.invoices,
      SECTION_CODES.vptasInvoiceListForm.invoiceSection,
      filter
    );

    let filterRequest: VptasInvoiceGridFilterRequest = this.filterQuestionToFilterModelMapper.Map(filterModel);

    if (filterRequest == null) {
      filterRequest = {
        applicationId: this.applicationId,
        personId: this.contactId
      };
    }
    else {
      filterRequest.applicationId = this.applicationId;
      filterRequest.personId = this.contactId;
    }

    return filterRequest;
  }

  private navigateBack() {
    const navExtras = this.getNavigationExtras();
    if (this.applicationId) {
      this.router.navigate(['/', ROUTE_CONSTANTS.vptasApplication.prefix, ROUTE_CONSTANTS.vptasApplication.details.prefix, this.applicationId], navExtras);
      return;
    }

    if (this.contactId) {
      this.router.navigate(['/', ROUTE_CONSTANTS.person.prefixDetail, this.contactId], navExtras);
      return;
    }

    this.router.navigate(['/', ROUTE_CONSTANTS.vptasApplication.list.myApplications], navExtras);
  }

  private getNavigationExtras(): NavigationExtras {
    return {
      state: {
        applicationFilterRequest: history.state.applicationFilterRequest,
        personFilterRequest: history.state.personFilterRequest,
      }
    };
  }
}
